// src/components/admin/AdminNav.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { closeOtherMenus, onCloseMenus } from '../../utils/menuEvents';
import { NavLink } from 'react-router-dom';
import api from '../../utils/api';

// inMenu: rendered inside the Header hamburger (mobile); the page-level bar is hidden below md
export default function AdminNav({ inMenu = false, onNavigate } = {}) {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false); // Small screens only: toggles the links
  // Opening this menu closes the profile and header menus; they can close it too
  // Inside the hamburger the parent menu stays open, so only the page-level nav asks others to close
  useEffect(() => { if (menuOpen && !inMenu) closeOtherMenus('admin-nav'); }, [menuOpen,inMenu]);
  useEffect(() => onCloseMenus('admin-nav', () => setMenuOpen(false)), []);
  // Tapping or clicking outside the nav closes the small-screen menu
  const navRef = useRef(null);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (e) => { if (!navRef.current?.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [menuOpen]);
  const [pendingCount, setPendingCount] = useState(0); // Pending Supplier-Admin approvals (superadmin badge)

  // Each page mounts its own nav, so the badge refreshes on every visit and whenever the approvals page changes the count
  // Re-render the admin links when the stored role is refreshed (see refreshCurrentUser)
  const [, setRoleTick] = useState(0);
  useEffect(() => {
    const onUpdate = () => setRoleTick((t) => t + 1);
    window.addEventListener('user-updated', onUpdate);
    return () => window.removeEventListener('user-updated', onUpdate);
  }, []);

  useEffect(() => {
    const role = JSON.parse(localStorage.getItem('user') || 'null')?.role;
    if (role !== 'superadmin') return undefined;

    const loadCount = () =>
      api.get('/api/users', { params: { role: 'pending' } })
        .then((res) => setPendingCount(Array.isArray(res.data) ? res.data.length : 0))
        .catch(() => setPendingCount(0));

    loadCount();
    window.addEventListener('pending-admins-changed', loadCount);
    return () => window.removeEventListener('pending-admins-changed', loadCount);
  }, []);

  // Retrieve the logged-in user's data from localStorage
  const user = JSON.parse(localStorage.getItem('user'));
  const role = user?.role;

  if (!role) return null; // Don't render anything if the user is not logged in

  const isSuperAdmin = role === 'superadmin';
  const isMember = role === 'member';
  // Members see admin links only when delegated to manage some groups
  const showAdminSection = !isMember || user?.delegatedGroupCount > 0;

  return (
    <nav ref={navRef} className={inMenu ? 'w-full' : 'hidden md:block bg-gray-200 border-b border-gray-300 shadow-sm w-full mb-6'}>
      <div className={`max-w-7xl mx-auto ${inMenu ? '' : 'px-4'} md:px-0 py-3 text-sm font-medium ${inMenu ? 'text-white' : 'text-gray-800'}`}>
        {/* Compact toggle on small screens; links always show inline from md up */}
        <button
          type="button"
          onClick={() => setMenuOpen((open) => !open)}
          aria-expanded={menuOpen}
          className={`md:hidden flex w-full items-center justify-between min-h-12 ${inMenu ? 'hover:text-green-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50' : ''}`}
        >
          <span>{inMenu && <i className="fas fa-user-cog mr-2" aria-hidden="true" />}{inMenu ? t('nav.administration') : t('nav.menu')}</span>
          <i className={`fas ${menuOpen ? 'fa-chevron-up' : 'fa-chevron-down'}`} />
        </button>
        <div onClick={() => { setMenuOpen(false); onNavigate?.(); }} className={`${menuOpen ? 'flex' : 'hidden'} md:flex ${inMenu ? '[&>a[aria-current=page]]:text-green-700' : ''} flex-col space-y-3 pt-3 md:pt-0 md:flex-row md:space-y-0 md:space-x-8 [&>a]:flex [&>a]:items-center [&>a]:w-full [&>a]:min-h-12 [&>a]:py-2 [&>a]:text-left md:[&>a]:min-h-0 md:[&>a]:py-0 md:[&>a]:w-auto`}>

        {/* Mobile-only heading that separates the admin links from customer links */}
        {showAdminSection && !inMenu && (
          <span className="md:hidden text-xs font-semibold uppercase tracking-wide text-gray-500">{t('nav.administration')}</span>
        )}

        {/* Supplier admins: their linked suppliers only (the page lists just those) */}
        {role === 'supplieradmin' && (
          <NavLink to="/admin/suppliers" className="hover:text-green-700 flex items-center space-x-1" title={t('nav.suppliers')}>
            <i className="fas fa-industry text-lg" />
            <span>{t('nav.suppliers')}</span>
          </NavLink>
        )}

        {/* Superadmin only */}
        {isSuperAdmin && (
          <>
            <NavLink
              to="/admin/suppliers"
              className="hover:text-green-700 flex items-center space-x-1"
              title={t('nav.suppliers')}
            >
              <i className="fas fa-industry text-lg" /> {/* FontAwesome icon */}
              <span>{t('nav.suppliers')}</span>
            </NavLink>
            <NavLink
              to="/admin/pending-admins"
              className="hover:text-green-700 flex items-center space-x-1"
              title={t('nav.pendingAdmins')}
            >
              <i className="fas fa-user-shield text-lg" /> {/* FontAwesome icon */}
              <span>{t('nav.pendingAdmins')}</span>
              {pendingCount > 0 && (
                <span className="bg-red-600 text-white text-xs font-semibold rounded-full px-2 py-0.5">{pendingCount}</span>
              )}
            </NavLink>
          </>
        )}

        {/* Common for both superadmin and supplieradmin; hidden from members */}
        {!isMember && (
          <>
            <NavLink
              to="/admin/create-group"
              className="hover:text-green-700 flex items-center space-x-1"
              title={t('nav.createGroup')}
            >
              <i className="fas fa-folder-plus text-lg" /> {/* FontAwesome icon */}
              <span>{t('nav.createGroup')}</span>
            </NavLink>
            <NavLink
              to="/admin/groups"
              className="hover:text-green-700 flex items-center space-x-1"
              title={t('nav.manageGroups')}
            >
              <i className="fas fa-cogs text-lg" /> {/* FontAwesome icon */}
              <span>{t('nav.manageGroups')}</span>
            </NavLink>
            <NavLink
              to="/admin/users"
              className="hover:text-green-700 flex items-center space-x-1"
              title={t('nav.manageUsers')}
            >
              <i className="fas fa-users text-lg" /> {/* FontAwesome icon */}
              <span>{t('nav.manageUsers')}</span>
            </NavLink>
          </>
        )}
        {/* Members delegated as Supplier Admin for some groups get Manage Groups only */}
        {isMember && user?.delegatedGroupCount > 0 && (
          <NavLink to="/admin/groups" className="hover:text-green-700 flex items-center space-x-1" title={t('nav.manageGroups')}>
            <i className="fas fa-cogs text-lg" /> {/* FontAwesome icon */}
            <span>{t('nav.manageGroupsDelegated')}</span>
          </NavLink>
        )}
        {/* Customer links, separated from the admin links on mobile */}
        {showAdminSection && <hr className="md:hidden border-gray-300" />}
        {/* Shown to every role, including members */}
        <NavLink
          to="/GroupSearchAndJoin"
          className="hover:text-green-700 flex items-center space-x-1"
          title={t('nav.searchGroups')}
        >
          <i className="fas fa-search text-lg" /> {/* FontAwesome icon */}
          <span>{t('nav.searchGroups')}</span>
        </NavLink>
        {/* Super admins view every supplier's orders here (read-only); supplier admins use the link above */}
        {isSuperAdmin && (
          <NavLink
            to="/admin/supplier-orders"
            className="hover:text-green-700 flex items-center space-x-1"
            title={t('nav.supplierOrders')}
          >
            <i className="fas fa-truck text-lg" /> {/* FontAwesome icon */}
            <span>{t('nav.supplierOrders')}</span>
          </NavLink>
        )}
        {role === 'supplieradmin' && (
              <NavLink
                to="/admin/supplier-orders"
                className="hover:text-green-700 flex items-center space-x-1"
                title={t('nav.supplierOrders')}
              >
                <i className="fas fa-truck text-lg" /> {/* FontAwesome icon */}
                <span>{t('nav.supplierOrders')}</span>
              </NavLink>
            )}
        {/* Shown to every role, including members */}
        <NavLink
          to="/my-orders"
          className="hover:text-green-700 flex items-center space-x-1"
          title={t('nav.myOrders')}
        >
          <i className="fas fa-receipt text-lg" /> {/* FontAwesome icon */}
          <span>{t('nav.myOrders')}</span>
        </NavLink>

        </div>
      </div>
    </nav>
  );
}
